import { useCallback, useMemo, useEffect } from "react"
import { useNavigate, useOutletContext } from "react-router-dom"
import { useDirection } from "hooks/direction/useDirection"
import { Outlet } from "react-router-dom"
import { ErrorDialog } from "components/Dialog"
import { useTranslation } from "providers/i18n"
import { Head } from "../../../../../components/Head"
import { getFeatureDetail } from "utils/feature"
import { useGeoLocation } from "providers/geolocation"
import { useCountdownTimer } from "hooks/useCountdownTimer"

export const DirectionLayoutKioskVPage = () => {
  const {
    route,
    isError,
    origin,
    destination,
    isCalculatingRoute,
    exitPath,
    elevatorPreferredMode,
    toggleElevatorPreference,
  } = useDirection()
  const { deviceLocation } = useGeoLocation()
  const { t } = useTranslation({ defaultValue: null })
  const { name: destinationName = "" } = useMemo(
    () => (destination ? getFeatureDetail(destination) : {}),
    [destination]
  )

  const { name: originName = "" } = useMemo(
    () => (origin ? getFeatureDetail(origin) : {}),
    [origin]
  )
  const { scannedLocations, userLocation } = useOutletContext()
  const navigate = useNavigate()

  const handleCloseDialog = useCallback(() => {
    navigate(exitPath)
  }, [navigate, exitPath])

  const { countdownTimer } = useCountdownTimer({
    onComplete: handleCloseDialog,
    options: {
      enable: isError,
    },
  })

  useEffect(() => {
    if (!origin && destination && deviceLocation) {
      navigate(
        `/maps/dir/${deviceLocation.properties.userLocationParam}/${destination?.id}`,
        { replace: true }
      )
    }
  }, [destination, origin, deviceLocation, navigate])

  return (
    <>
      <Head
        title={`${t("title.direction", {
          origin: t(originName),
          destination: t(destinationName),
        })}`}
      />
      <ErrorDialog
        open={isError}
        title={t("Navigation Error", { ns: "error" })}
        description={t(
          "The system was unable navigate because either the origin or the destination was not found which you have chosen.",
          { ns: "error" }
        )}
        onClose={handleCloseDialog}
        buttonText={`${t("Close")} (${countdownTimer})`}
      />
      {
        <Outlet
          context={
            route
              ? {
                  ...route,
                  route,
                  userLocation,
                  scannedLocations,
                  elevatorPreferredMode,
                  isCalculatingRoute,
                  toggleElevatorPreference,
                }
              : {
                  route,
                  origin,
                  destination,
                  scannedLocations,
                  elevatorPreferredMode,
                  isCalculatingRoute,
                  toggleElevatorPreference,
                }
          }
        />
      }
    </>
  )
}
